import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { Component, ElementRef, Input, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import {
  isNotNullOrUndefined,
  RoleService,
  System,
  ProductModelService,
} from 'ngx-core-services'
import { format } from 'date-fns';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';

import {
  requestSystemAlerts,
  selectedSystemDevices,
  selectedSystemMonitorPref,
  selectedSystemTz,
  selectSystemAlertsCounterBySystemIdAcrossInstances,
  selectedSystemUid,
  SystemsState,
} from '../../../store';
import { CreateBlockReduxSupport, IReduxBlock } from '../../../store/helpers/decorator';
import { AlertContent, AlertDataSource, AlertMapForList } from '../alert.model';
import { selectSystemDailyAlertsBySelectedSystem, selectSystemDailyAlertsStatusBySelectedSystem } from '../../../store/selectors/alerts-list.selectors';

const labels = {
  codeHeader: 'Code',
  filter: 'Filter',
  descriptionHeader: 'Description',
  inverterHeader: 'Inverter',
  importanceHeader: 'Importance',
  tableNoData: 'No alerts found',
  alertNotice: 'Notice',
  alertWarning: 'Warning',
  alertError: 'Error',
  loading: 'Loading ...',
  title: 'Alerts',
  powerMagicAreNotSupported: 'Power Magic device alerts are supported only with Azzurro Hub monitoring',
  currentAlert: 'Current',
  timelineAlertPresent: 'Alert present',
  timelineAlertNotPresent: 'Alert not present',
  timelineTelemetryAbsent: 'Telemetry unavailable',
};

@CreateBlockReduxSupport()
@Component({
  selector: 'lib-alerts-list',
  templateUrl: './alerts-list.component.html',
  styleUrls: ['./alerts-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class AlertsListComponent implements IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  @Input() expandTemplate?: TemplateRef<any>;

  readonly _labels = labels;
  @Input() set labels(value: typeof this._labels) {
    Object.assign(this._labels, value);
  }
  get labels() {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  displayedColumns: string[] = ['faultCode', 'description', 'serial', 'level'];

  readonly _instanceId$: Observable<string> = new Observable<string>();

  @ViewChild(MatSort) sort?: MatSort;
  readonly invertersFilter$ = new BehaviorSubject<string[]>([]);
  readonly codesFilter$ = new BehaviorSubject<string[]>([]);
  readonly filter$ = new BehaviorSubject<string>('');
  readonly requestStatus$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectSystemDailyAlertsStatusBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
  );

  readonly selectSystemDailyAlertsBySelectedSystem$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectSystemDailyAlertsBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
  );

  readonly dateSelection$ = new BehaviorSubject<string>(format(new Date(), 'yyyy-MM-dd'));
  readonly systemTz$ = this.store.select(selectedSystemTz).pipe(isNotNullOrUndefined());
  expandedElementId = 'none';

  readonly canViewAllAlerts$ = this.roleService.has_somePermission$;

  readonly requestAlerts$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this.dateSelection$,
    this._refresh$,
    this._instanceId$,
    this.canViewAllAlerts$,
  ]).pipe(
    tap(([systemId, date, forceRefresh, instanceId, canViewAllAlerts]) => {
      const range = {
        from: date,
        to: date,
      };
      this.store.dispatch(requestSystemAlerts({ systemId, instanceId, range, forceRefresh, showAll: canViewAllAlerts }));
    }),
  );

  readonly requestIsPending$ = this.requestStatus$.pipe(
    map((status) => status === 'pending'),
    startWith(false),
    isNotNullOrUndefined(),
  );


  readonly inverters$ = this.store.select(selectedSystemDevices(['inverter'])).pipe(
    isNotNullOrUndefined(),
    map((devices) => devices.map((device) => device.id.toUpperCase())),
  );

  readonly showPowerMagicAlert$ = combineLatest([
    this.inverters$,
    this.store.select(selectedSystemMonitorPref).pipe(isNotNullOrUndefined()),
  ]).pipe(
    map(([inverters, monitorPref]) => {
      const hasPowerMagic = inverters.some((id) => new ProductModelService(id).isPowerMagic);
      return hasPowerMagic && monitorPref !== 'AzzurroHub';
    }),
  );

  readonly currentAlerts$ = this.system$.pipe(
    isNotNullOrUndefined(),
    switchMap((system) =>
      this.store.select(selectSystemAlertsCounterBySystemIdAcrossInstances(system.uid!))
    ),
    map((alerts) => alerts || {}),
  );

  readonly systemAlerts$ = combineLatest([
    this.selectSystemDailyAlertsBySelectedSystem$,
    this.systemTz$,
    this.currentAlerts$,
  ]).pipe(
    map(([alerts, timezone, currentAlerts]) => {
      return {
        alerts,
        timezone,
        currentAlerts,
      }
    }),
  );

  readonly tableAlerts$ = combineLatest([
    this.systemAlerts$,
    this.filter$.pipe(startWith('')),
  ]).pipe(
    map(([systemAlerts, filter]) => {
      const dataTable = this.adaptDataForTable(
        systemAlerts.alerts.alerts,
        systemAlerts.alerts.allTimestampsBySerial,
        systemAlerts.currentAlerts,
      );
      const matTableDataSource = new MatTableDataSource(dataTable);
      // workaround for mat-table not updating sort when data is set
      setTimeout(() => {
        if (this.sort) {
          matTableDataSource.sort = this.sort;
        }
      }, 100);
      matTableDataSource.filter = filter.trim().toLowerCase();
      return matTableDataSource;
    }),
  );

  readonly listAlerts$ = combineLatest([
    this.systemAlerts$,
    this.invertersFilter$.pipe(startWith([])),
    this.codesFilter$.pipe(startWith([])),
  ]).pipe(
    map(([systemAlerts, inverters]) => this.adaptForList(systemAlerts.alerts.alerts, inverters, systemAlerts.currentAlerts)),
  );

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    private readonly roleService: RoleService,
  ) { }

  convertLevelToLabel(level: number | undefined) {
    switch (level) {
      case 1:
        return labels.alertWarning;
      case 2:
        return labels.alertError;
      default:
        return labels.alertNotice;
    }
  }

  getLevelClass(
    prefix: string,
    level: number | undefined,
    enabled: boolean = true,
  ): string {
    if (!enabled) {
      return '';
    }

    const normalizedLevel = level === 1 || level === 2 ? level : 0;
    return `${prefix}-level-${normalizedLevel}`;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filter$.next(filterValue);
  }

  adaptDataForTable(
    data: AlertContent,
    allTimestampsBySerial: { [serial: string]: number[] },
    currentAlerts: AlertContent,
  ): AlertDataSource[] {
    const result: AlertDataSource[] = [];
    for (const faultCode in data) {
      if (!data.hasOwnProperty(faultCode)) {
        continue;
      }

      for (const serial in data[faultCode].serials) {
        if (!data[faultCode].serials.hasOwnProperty(serial)) {
          continue;
        }

        const events = data[faultCode].serials[serial];
        const details = data[faultCode].details;
        result.push({
          ...details,
          serial,
          events,
          faultCode,
          tableId: `${serial}-${faultCode}`,
          allTimestamps: allTimestampsBySerial[serial] || [],
          isCurrent: this.isCurrentAlert(currentAlerts, faultCode, serial),
        });
      }
    }
    return result;
  }

  adaptForList(data: AlertContent, inverters: string[], currentAlerts: AlertContent) {
    const result: AlertMapForList = {};

    for (const faultCode in data) {
      if (!data.hasOwnProperty(faultCode)) {
        continue;
      }

      for (const serial in data[faultCode].serials) {
        if (!data[faultCode].serials.hasOwnProperty(serial)) {
          continue;
        }

        const useInverter = inverters.length === 0 || inverters.includes(serial);
        if (!useInverter) {
          continue;
        }

        if (!result[serial]) {
          result[serial] = [];
        }

        const details = data[faultCode].details;
        result[serial].push({
          alert: {
            ...details,
            faultCode,
          },
          isCurrent: this.isCurrentAlert(currentAlerts, faultCode, serial),
        });
      }
    }

    return {
      data: result,
      noData: Object.keys(result).length === 0,
    };
  }

  isCurrentAlert(currentAlerts: AlertContent, faultCode: string, serial: string): boolean {
    return Boolean(currentAlerts[faultCode]?.serials?.[serial]?.length);
  }

  toggleRow(row: AlertDataSource) {
    this.expandedElementId = row.tableId === this.expandedElementId ? 'none' : row.tableId;
  }
}
